/**
 * Marketing-facing plan specs — the single source of truth for the numbers
 * shown on prerendered marketing surfaces (landing pricing teaser, FAQ copy)
 * AND consumed by the API's `plans:seed` script so the teaser can never drift
 * from what the database bills.
 *
 * Pricing is minute-based: plans include inbound voice minutes + chat
 * messages. There is no per-call allowance (`callsIncluded` in the DB is a
 * retired legacy stat, never displayed).
 *
 * Changing a price here requires re-running
 * `npm --workspace @platform/api run plans:seed -- --sync-stripe`.
 */
export interface MarketingPlanSpec {
  slug: string;
  name: string;
  priceCents: number;
  currency: string;
  /** Inbound voice minutes included per month (CALL_MINUTES quota). */
  voiceMinutesIncluded: number;
  /** Chat messages included per month (CHAT_MESSAGE quota). */
  chatMessagesIncluded: number;
  /** Visually emphasized tier on marketing surfaces. */
  highlighted: boolean;
  /**
   * Opt-in overage rates (Phase C — docs/APP-MARKETING-SENDER-LOCK-OVERAGE-PLAN.md),
   * confirmed 2026-07-10. Voice is billed per extra minute. Chat is billed in
   * whole blocks of 1,000 extra messages — crossing the quota owes the full
   * block price (ceil), there is no per-message fraction.
   */
  overagePerMinuteCents: number;
  overagePer1000MessagesCents: number;
}

export const MARKETING_PLAN_SPECS: readonly MarketingPlanSpec[] = [
  {
    slug: 'starter',
    name: 'Starter',
    priceCents: 3900,
    currency: 'usd',
    voiceMinutesIncluded: 50,
    chatMessagesIncluded: 500,
    highlighted: false,
    overagePerMinuteCents: 49,
    overagePer1000MessagesCents: 1200,
  },
  {
    slug: 'growth',
    name: 'Growth',
    priceCents: 9900,
    currency: 'usd',
    voiceMinutesIncluded: 200,
    chatMessagesIncluded: 2500,
    highlighted: true,
    overagePerMinuteCents: 45,
    overagePer1000MessagesCents: 1100,
  },
  {
    slug: 'scale',
    name: 'Scale',
    priceCents: 24900,
    currency: 'usd',
    voiceMinutesIncluded: 600,
    chatMessagesIncluded: 10000,
    highlighted: false,
    overagePerMinuteCents: 42,
    overagePer1000MessagesCents: 1000,
  },
] as const;

/** "25 voice minutes · 200 chat messages / mo" — shared quota line format. */
export function marketingQuotaLine(spec: MarketingPlanSpec): string {
  return `${spec.voiceMinutesIncluded} voice minutes · ${spec.chatMessagesIncluded.toLocaleString('en-US')} chat messages / mo`;
}
